import React, { ChangeEvent, DragEvent, ReactElement, useRef } from 'react';
import {
  AddPhotoAlternate,
  CloudUploadOutlined,
  ErrorOutline,
  InfoOutlined,
} from '@mui/icons-material';
import { Box, Button, Typography } from '@mui/material';

import {
  gray100,
  gray80,
  red50,
  orange30,
  orange50,
  white,
  blue20,
  blue50,
} from '../../../constants/colors';
import { formLabelWrapper, regularMd, regularSm, regularXs } from '../../../constants/location';

type Props = {
  helperText: string | undefined;
  onFilesSelected: (files: FileList | null) => void;
  onButtonClick?: () => boolean; // ボタン押下時のコールバック（falseを返すとファイル選択をキャンセル）
  multiple?: boolean;
  labelText?: string;
  requiredText?: string;
  labelFontSize?: string;
  height?: string;
  fileCountOverText?: string;
  disabled?: boolean;
  showNoteInside?: boolean; // 注意書きをグレーエリア内に表示するか
  noteText?: string; // 注意書きのテキスト
  infoText?: string; // 情報メッセージ（警告ではなく情報トーンで表示。例: クレジットから消費される旨）
  accept?: string; // input[type=file] の accept 属性（例: '.csv,text/csv'）。未指定なら制限なし
};

const FileUploader = ({
  helperText,
  onFilesSelected,
  onButtonClick,
  labelText,
  requiredText,
  labelFontSize,
  multiple = true,
  height,
  fileCountOverText,
  disabled = false,
  showNoteInside = false,
  noteText,
  infoText,
  accept,
}: Props): ReactElement => {
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  if (helperText == undefined && fileCountOverText !== '') {
    helperText = fileCountOverText;
  }
  const styles = {
    button: {
      height: '44px',
      padding: '16px',
      '& .MuiButton-icon': {
        marginRight: '4px',
      },
    },
    errorText: {
      ...regularXs,
      color: red50,
      marginTop: '4px',
    },
    icon24: {
      height: '24px',
      marginLeft: '0px',
      width: '24px',
    },
    input: {
      display: 'none',
    },
    text: {
      ...regularMd,
      color: gray80,
      '@media screen and (min-width: 960px)': {
        ...regularSm,
      },
    },
    uploadArea: {
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: '#f9f9f9',
      border: '1px dashed #d9d9d9',
      borderRadius: '10px',
      boxSizing: 'border-box',
      display: 'flex',
      flexDirection: 'column',
      gap: '8px',
      padding: '16px 0',
      width: '100%',
      ...(height && { minHeight: height }),
    },
    wrapper: {
      marginBottom: '24px',
      '@media screen and (min-width: 960px)': {
        marginBottom: '16px',
      },
    },
    formLabelWrapper,
    label: {
      fontSize: labelFontSize ? labelFontSize : '14px',
    },
    required: {
      color: red50,
      fontSize: labelFontSize ? labelFontSize : '14px',
      marginLeft: '6px',
    },
  };

  // ファイルを選択するボタン押下時
  const handleButtonClick = (): void => {
    if (disabled) return;

    // コールバックがある場合は実行し、falseが返されたらファイル選択をキャンセル
    if (onButtonClick && !onButtonClick()) {
      return;
    }
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  // ファイル選択時
  const handleFileChange = (event: ChangeEvent<HTMLInputElement>): void => {
    if (disabled) return;
    const files = event.target.files;
    onFilesSelected(files);
    // ファイル選択後に input の value をリセット（同じファイルを再選択可能にする）
    if (event.target) {
      event.target.value = '';
    }
  };

  const handleDrop = (event: DragEvent<HTMLDivElement>): void => {
    event.preventDefault();
    if (disabled) return;
    const files = event.dataTransfer.files;
    onFilesSelected(files);
  };

  const handleDragOver = (event: DragEvent<HTMLDivElement>): void => {
    event.preventDefault();
  };

  return (
    <Box sx={styles.wrapper}>
      {labelText && (
        <Typography align="left" sx={styles.formLabelWrapper} component="div">
          <Typography color={gray80} sx={styles.label} component="span">
            {labelText}
          </Typography>

          {requiredText && (
            <Typography
              component="span"
              sx={{
                ...styles.required,
                textAlign: 'left',
                display: 'inline-block',
              }}
            >
              {requiredText}
            </Typography>
          )}
        </Typography>
      )}
      {helperText && showNoteInside && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            backgroundColor: 'rgba(202, 138, 4, 0.1)',
            padding: '6px 16px',
            borderRadius: '4px',
            marginBottom: '16px',
          }}
        >
          <ErrorOutline sx={{ fontSize: '22px', color: '#ca8a04' }} />
          <Typography
            sx={{
              fontSize: '14px',
              color: '#ca8a04',
              lineHeight: '1.43',
              letterSpacing: '0.17px',
              whiteSpace: 'pre-line',
            }}
          >
            {helperText}
          </Typography>
        </Box>
      )}
      {/* 情報メッセージ（禁止操作と誤認されないよう、警告色ではなく情報トーンで表示） */}
      {infoText && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            backgroundColor: blue20,
            padding: '6px 16px',
            borderRadius: '4px',
            marginBottom: '16px',
          }}
        >
          <InfoOutlined sx={{ fontSize: '22px', color: blue50, flexShrink: 0 }} />
          <Typography
            sx={{
              fontSize: '14px',
              color: blue50,
              lineHeight: '1.43',
              letterSpacing: '0.17px',
              whiteSpace: 'pre-line',
            }}
          >
            {infoText}
          </Typography>
        </Box>
      )}
      <Box onDrop={handleDrop} onDragOver={handleDragOver} sx={styles.uploadArea}>
        <input
          accept={accept}
          multiple={multiple}
          onChange={handleFileChange}
          ref={fileInputRef}
          style={styles.input}
          type="file"
        />
        <AddPhotoAlternate sx={{ fontSize: '60px', color: gray80 }} />
        <Typography
          sx={{
            ...regularMd,
            color: gray100,
            fontWeight: 500,
            fontSize: '16px',
            lineHeight: '1.4',
          }}
        >
          ファイルをここにドラッグ＆ドロップ
        </Typography>
        <Typography
          sx={{
            ...regularSm,
            color: gray100,
            fontSize: '14px',
            lineHeight: '1.4',
          }}
        >
          または
        </Typography>
        <Button
          onClick={handleButtonClick}
          disabled={disabled}
          startIcon={<CloudUploadOutlined sx={{ fontSize: '24px', color: orange50 }} />}
          sx={{
            height: '40px',
            padding: '8px 24px',
            border: `1px solid ${orange50}`,
            borderRadius: '6px',
            backgroundColor: white,
            color: orange50,
            boxShadow: 'none',
            '&:hover': {
              backgroundColor: white,
              border: `1px solid ${orange30}`,
              boxShadow: 'none',
              opacity: '80%',
              '& .MuiTypography-root': {
                color: orange30,
              },
              '& .MuiSvgIcon-root': {
                color: orange30,
              },
            },
          }}
        >
          <Typography sx={{ fontSize: '16px', fontWeight: 500, color: orange50 }}>
            ファイルを選択する
          </Typography>
        </Button>
        {showNoteInside && noteText && (
          <Typography
            sx={{
              fontSize: '12px',
              color: '#717182',
              textAlign: 'left',
              lineHeight: '1.8',
              marginTop: '4px',
              whiteSpace: 'pre-line',
              padding: '0 24px',
            }}
          >
            {noteText}
          </Typography>
        )}
      </Box>
      {helperText && !showNoteInside && (
        <Typography sx={{ ...styles.errorText, textAlign: 'left', whiteSpace: 'pre-line' }}>
          {helperText}
        </Typography>
      )}
    </Box>
  );
};

export default FileUploader;
